import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth';
import { useCurrentProfile } from '../../shared/CurrentProfileProvider';
import { EventDetailsStep, parseDuration } from './EventDetailsStep';
import { DateTimePicker } from './DateTimePicker';
import { ModelPaymentStep } from './ModelPaymentStep';
import { PublishSection } from './PublishSection';
import { CreateFormState, initialFormState } from './create.types';
import { isSupportedCurrency, normalizeCurrency } from '../../shared/money';
import { NavBar } from '../../components/NavBar';
import type { RevenueSplitMode, Room } from '@modelcast/types';
import type { SelectedModel } from './create.types';
import { authHeaders, jsonAuthHeaders } from '../../shared/authHeaders';

interface LocationState {
  formState?: CreateFormState;
  roomCode?: string;
  section?: string;
}

type Section = 'details' | 'model' | 'publish';
type DuplicateSessionFilter = 'all' | 'tutored' | 'untutored';

interface DuplicateSourceRoom {
  id: string;
  role?: 'hosting' | 'modeling' | 'attending';
  code: string;
  name: string;
  description: string | null;
  scheduledAt: string | null;
  endsAt: string | null;
  startedAt: string | null;
  closedAt: string | null;
  imageUrl: string | null;
  isPublished: boolean;
  isTutored: boolean;
  seatsConfirmed: number;
  modelName: string | null;
}

const SECTIONS = [
  { id: 'details', label: 'Event Details', icon: '1' },
  { id: 'model', label: 'Pricing & Model', icon: '2' },
  { id: 'publish', label: 'Publish', icon: '3' },
] as const;

async function getErrorMessage(response: Response, fallback: string) {
  const text = await response.text().catch(() => '');
  if (!text) {
    return response.status === 500
      ? `${fallback}: the API server did not return an error body. Make sure the backend is running and restart it if it recently crashed.`
      : `${fallback} (${response.status})`;
  }

  try {
    const data = JSON.parse(text);
    return data.detail || data.error || data.details?.[0]?.message || `${fallback} (${response.status})`;
  } catch {
    return `${fallback} (${response.status}): ${text.slice(0, 240)}`;
  }
}

function normalizeSection(section?: string): Section {
  if (section === 'details' || section === 'model' || section === 'publish') {
    return section;
  }
  return 'details';
}

function getRoomCodeFromCreateSearch(search: string): string | null {
  const params = new URLSearchParams(search);
  return params.get('roomCode') || params.get('room') || null;
}

function getSectionFromCreateSearch(search: string): string | undefined {
  return new URLSearchParams(search).get('section') ?? undefined;
}

function getCreateUrl(roomCode: string | null, section: Section): string {
  if (!roomCode) return '/create';

  const params = new URLSearchParams({ roomCode });
  if (section !== 'details') {
    params.set('section', section);
  }

  return `/create?${params.toString()}`;
}

function normalizeModelOption(option?: Room['modelOption']): CreateFormState['modelOption'] {
  if (option === 'self' || option === 'invite' || option === 'ad') {
    return option;
  }

  return 'none';
}

function roomModelToSelectedModel(model: NonNullable<Room['models']>[number]): SelectedModel {
  return {
    id: model.id,
    profileId: model.profileId,
    email: model.email,
    displayName: model.displayName,
    photoUrl: null,
    source: model.source,
    status: model.status,
    splitPercent: model.splitPercent,
    guarantee: model.guarantee !== null ? model.guarantee / 100 : null,
    inviteId: model.inviteId,
    rsvpStatus: model.status === 'accepted'
      ? 'confirmed'
      : model.status === 'declined'
        ? 'declined'
        : 'pending',
  };
}

function isJobApplicantRow(model: SelectedModel): boolean {
  return model.source === 'application' && !model.profileId && !model.email;
}

function isKnownModelInviteRow(model: SelectedModel): boolean {
  return model.source !== 'organizer_self' && !isJobApplicantRow(model);
}

function getModelOptionForRows(models: SelectedModel[]): CreateFormState['modelOption'] {
  if (models.length === 0) return 'none';
  if (models.some(isJobApplicantRow)) return 'ad';
  if (models.length === 1 && models[0]?.source === 'organizer_self') return 'self';
  return 'invite';
}

function getRevenueSplitMode(room?: Room | null): RevenueSplitMode {
  return room?.settings?.revenueSplitMode === 'even' ? 'even' : 'manual';
}

function getSelectedModelsForRequest(formState: CreateFormState): SelectedModel[] {
  if (formState.selectedModels.length > 0) return formState.selectedModels;
  if (formState.modelOption === 'self' && formState.selectedModels.length === 0) {
    return [{
      profileId: null,
      email: null,
      displayName: 'Organizer',
      photoUrl: null,
      source: 'organizer_self',
      splitPercent: formState.modelSplitPercent,
      guarantee: null,
    }];
  }

  return formState.selectedModels;
}

function getTotalModelGuaranteeCents(models: SelectedModel[]) {
  const total = models.reduce((sum, model) => (
    sum + (model.source === 'organizer_self' ? 0 : Math.round((model.guarantee ?? 0) * 100))
  ), 0);
  return total > 0 ? total : null;
}

function clampPercent(value: number | null | undefined): number {
  const numericValue = Number(value ?? 0);
  if (!Number.isFinite(numericValue)) return 0;
  return Math.max(0, Math.min(100, Math.round(numericValue)));
}

function hasCompleteRevenueSplit(formState: CreateFormState, selectedModels: SelectedModel[]) {
  if (
    formState.pricingType === 'free' ||
    selectedModels.length === 0 ||
    formState.revenueSplitMode === 'even'
  ) {
    return true;
  }

  const externalModelSplit = selectedModels.reduce((sum, model) => (
    model.source === 'organizer_self' ? sum : sum + clampPercent(model.splitPercent)
  ), 0);
  if (externalModelSplit > 100) return false;

  const organizerModel = selectedModels.find(model => model.source === 'organizer_self');
  const organizerSplit = organizerModel
    ? clampPercent(organizerModel.splitPercent)
    : 100 - externalModelSplit;

  return externalModelSplit + organizerSplit === 100;
}

function getEvenSplitRecipientCount(models: SelectedModel[]) {
  if (models.length === 0) return 0;
  const organizerIsModel = models.some(model => model.source === 'organizer_self');
  return models.length + (organizerIsModel ? 0 : 1);
}

function getLegacyModelSplitPercent(formState: CreateFormState, selectedModels: SelectedModel[]) {
  if (formState.revenueSplitMode !== 'even') {
    return selectedModels.reduce((sum, model) => sum + (model.splitPercent ?? 0), 0);
  }

  const recipientCount = getEvenSplitRecipientCount(selectedModels);
  if (recipientCount === 0) return 0;
  const organizerIsModel = selectedModels.some(model => model.source === 'organizer_self');
  return organizerIsModel
    ? 100
    : Math.round((selectedModels.length / recipientCount) * 100);
}

function formatDurationString(minutes: number) {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

function getRoomDurationString(scheduledAt: string | null, endsAt: string | null, fallbackMinutes?: number | null) {
  const fallback = fallbackMinutes && fallbackMinutes > 0 ? fallbackMinutes : null;
  if (!scheduledAt || !endsAt) return fallback ? formatDurationString(fallback) : '';

  const start = new Date(scheduledAt).getTime();
  const end = new Date(endsAt).getTime();
  const diffMinutes = Math.round((end - start) / (1000 * 60));

  if (!Number.isFinite(diffMinutes) || diffMinutes <= 0) {
    return fallback ? formatDurationString(fallback) : '';
  }

  return formatDurationString(diffMinutes);
}

function getDuplicateDateTimeMin() {
  const date = new Date(Date.now() + 5 * 60 * 1000);
  date.setSeconds(0, 0);
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function isPastDuplicateSourceRoom(room: DuplicateSourceRoom, now = new Date()) {
  if (room.role !== 'hosting') return false;
  if (room.closedAt) return true;

  const endsAt = room.endsAt ? new Date(room.endsAt) : null;
  if (endsAt && Number.isFinite(endsAt.getTime()) && endsAt < now) return true;

  const scheduledAt = room.scheduledAt ? new Date(room.scheduledAt) : null;
  return Boolean(scheduledAt && Number.isFinite(scheduledAt.getTime()) && scheduledAt < now);
}

function formatSessionDate(value: string | null) {
  if (!value) return 'No date';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'No date';

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatSessionTime(value: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';

  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function CreateEventPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();
  const { defaultEventCurrency } = useCurrentProfile();
  const locationState = location.state as LocationState | null;
  const locationRoomCode = locationState?.roomCode || getRoomCodeFromCreateSearch(location.search);
  const locationSection = locationState?.section || getSectionFromCreateSearch(location.search);

  const [section, setSection] = useState<Section>(
    locationRoomCode ? normalizeSection(locationSection) : 'details'
  );
  const [formState, setFormState] = useState<CreateFormState>(
    locationState?.formState || initialFormState
  );
  const [roomCode, setRoomCode] = useState<string | null>(locationRoomCode);
  const [roomId, setRoomId] = useState<string | null>(null);
  const [room, setRoom] = useState<Room | null>(null);
  const [publishDraft, setPublishDraft] = useState({
    isPublished: false,
    isPublic: false,
    isModelAdPublished: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showEventLiveModal, setShowEventLiveModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateRooms, setDuplicateRooms] = useState<DuplicateSourceRoom[]>([]);
  const [duplicateSearch, setDuplicateSearch] = useState('');
  const [duplicateFilter, setDuplicateFilter] = useState<DuplicateSessionFilter>('all');
  const [selectedDuplicateCode, setSelectedDuplicateCode] = useState<string | null>(null);
  const [duplicateDateTime, setDuplicateDateTime] = useState('');
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const isEditing = !!roomCode;

  useEffect(() => {
    if (!roomCode) return;

    const nextUrl = getCreateUrl(roomCode, section);
    const currentUrl = `${location.pathname}${location.search}`;
    if (currentUrl === nextUrl) return;

    navigate(nextUrl, {
      replace: true,
      state: location.state || undefined,
    });
  }, [roomCode, section, location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    const nextRoomCode = getRoomCodeFromCreateSearch(location.search);
    if (!nextRoomCode || nextRoomCode === roomCode) return;

    setRoomCode(nextRoomCode);
    setRoomId(null);
    setRoom(null);
    setSection(normalizeSection(getSectionFromCreateSearch(location.search)));
    setHasUnsavedChanges(false);
  }, [location.search, roomCode]);

  // Fetch room when we have a room code
  useEffect(() => {
    if (roomCode) {
      fetchRoom();
    }
  }, [roomCode]);

  // Default the currency for brand-new events: the organizer's last-used
  // event currency, otherwise one inferred from their location (the API
  // decides — see profiles/me defaultEventCurrency). Never overrides a
  // currency the organizer has already interacted with.
  useEffect(() => {
    if (isEditing || locationState?.formState) return;
    if (!isSupportedCurrency(defaultEventCurrency)) return;

    setFormState(prev => (
      prev.ticketPrice === null && prev.suggestedMinimum === null && prev.currency === 'USD'
        ? { ...prev, currency: normalizeCurrency(defaultEventCurrency) }
        : prev
    ));
  }, [defaultEventCurrency, isEditing, locationState?.formState]);

  // Populate form state from room data when editing
  useEffect(() => {
    if (room && isEditing && !locationState?.formState) {
      const durationString = getRoomDurationString(room.scheduledAt, room.endsAt, room.duration);

      setFormState(prev => {
        const roomSelectedModels = (room.models ?? []).map(roomModelToSelectedModel);
        const selectedModels = prev.selectedModels.length > 0 ? prev.selectedModels : roomSelectedModels;

        return {
          name: room.name || '',
          description: room.description || '',
          scheduledAt: room.scheduledAt || '',
          duration: durationString,
          timezone: room.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          isPublic: room.isPublic || false,
          isTutored: room.isTutored || false,
          maxAttendees: room.maxAttendees || null,
          eventImage: null,
          existingImageUrl: room.imageUrl || null,
          imageArtistCredit: room.imageArtistCredit || '',
          imageModelCredit: room.imageModelCredit || '',
          modelOption: selectedModels.length > 0
            ? getModelOptionForRows(selectedModels)
            : normalizeModelOption(room.modelOption),
          selectedModel: selectedModels[0] ?? prev.selectedModel,
          selectedModels,
          modelExpectations: room.modelExpectations || '',
          modelAdText: room.modelAdText || '',
          pricingType: room.pricingType || 'donation',
          currency: normalizeCurrency(room.currency),
          ticketPrice: room.ticketPrice !== null ? room.ticketPrice / 100 : null, // Convert cents to major units
          suggestedMinimum: room.suggestedMinimum !== null ? room.suggestedMinimum / 100 : null,
          modelSplitPercent: room.modelSplitPercent ?? 75,
          modelGuarantee: room.modelGuarantee ? room.modelGuarantee / 100 : null, // Convert cents to dollars
          revenueSplitMode: getRevenueSplitMode(room),
        };
      });
    }
  }, [room, isEditing, locationState?.formState]);

  useEffect(() => {
    if (!room) return;

    setPublishDraft({
      isPublished: room.isPublished,
      isPublic: room.isPublic,
      isModelAdPublished: room.isModelAdPublished,
    });
  }, [room?.id, room?.isPublished, room?.isPublic, room?.isModelAdPublished]);

  const fetchRoom = async (codeOverride?: string) => {
    const targetRoomCode = codeOverride ?? roomCode;
    if (!targetRoomCode) return;
    try {
      const token = await getToken();
      const response = await fetch(`/api/rooms/${targetRoomCode}`, {
        headers: authHeaders(token),
      });
      if (response.ok) {
        const data = await response.json();
        setRoom(data.room);
        setRoomId(data.room.id);

        const selectedModels = ((data.room.models ?? []) as NonNullable<Room['models']>).map(roomModelToSelectedModel);
        if (selectedModels.length > 0) {
          setFormState(prev => ({
            ...prev,
            modelOption: getModelOptionForRows(selectedModels),
            selectedModel: selectedModels[0] ?? null,
            selectedModels,
            revenueSplitMode: getRevenueSplitMode(data.room),
          }));
        } else if (data.modelProfile) {
          setFormState(prev => ({
            ...prev,
            selectedModel: {
              profileId: data.modelProfile.id,
              email: null, // Email comes from the profile's userId, not stored separately
              displayName: data.modelProfile.displayName,
              photoUrl: data.modelProfile.photos?.[0]?.url || null,
              source: 'invite',
              splitPercent: prev.modelSplitPercent,
              guarantee: 0,
            },
            selectedModels: [{
              profileId: data.modelProfile.id,
              email: null,
              displayName: data.modelProfile.displayName,
              photoUrl: data.modelProfile.photos?.[0]?.url || null,
              source: 'invite',
              splitPercent: prev.modelSplitPercent,
              guarantee: 0,
            }],
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching room:', error);
    }
  };

  const handleUpdate = (updates: Partial<CreateFormState>) => {
    setFormState((prev) => ({ ...prev, ...updates }));
    setHasUnsavedChanges(true);
  };

  const saveRoom = async (visibility?: {
    isPublished: boolean;
    isPublic: boolean;
    isModelAdPublished: boolean;
  }, options?: {
    requireModelInviteEmails?: boolean;
  }): Promise<string | null> => {
    setIsSubmitting(true);

    try {
      const token = await getToken();
      const effectiveIsPublished = visibility?.isPublished ?? room?.isPublished ?? false;
      const effectiveIsPublic = visibility?.isPublic ?? room?.isPublic ?? formState.isPublic;
      const ticketPriceCents = formState.pricingType === 'free'
        ? 0
        : formState.pricingType === 'fixed' && formState.ticketPrice !== null
          ? Math.round(formState.ticketPrice * 100)
          : null;
      const suggestedMinimumCents = formState.pricingType === 'donation' && formState.suggestedMinimum !== null
        ? Math.round(formState.suggestedMinimum * 100)
        : null;
      const selectedModels = getSelectedModelsForRequest(formState);
      const requestModelOption = getModelOptionForRows(selectedModels);
      if (requestModelOption === 'ad' && !formState.modelAdText.trim()) {
        alert('Please provide details of the session to potential applicants');
        return null;
      }

      const effectiveIsModelAdPublished = requestModelOption === 'ad'
        ? visibility?.isModelAdPublished ?? room?.isModelAdPublished ?? false
        : false;
      const totalModelSplitPercent = getLegacyModelSplitPercent(formState, selectedModels);
      const totalModelGuaranteeCents = getTotalModelGuaranteeCents(selectedModels);
      if (!hasCompleteRevenueSplit(formState, selectedModels)) {
        alert('Revenue split must add up to 100% before continuing.');
        return null;
      }

      const hasExternalModels = selectedModels.some((model) => model.source !== 'organizer_self');
      const hasKnownModelInviteRows = selectedModels.some(isKnownModelInviteRow);
      const modelGuaranteeCents = (formState.pricingType === 'free' || !hasExternalModels)
        ? null
        : totalModelGuaranteeCents;
      const requestBody: Record<string, unknown> = {
        name: formState.name,
        description: formState.description,
        isPublic: effectiveIsPublic,
        isTutored: formState.isTutored,
        isPublished: effectiveIsPublished,
        isModelAdPublished: effectiveIsModelAdPublished,
        settings: {
          ...(room?.settings ?? {}),
          revenueSplitMode: formState.revenueSplitMode,
        },
        modelOption: requestModelOption,
        modelExpectations: hasKnownModelInviteRows ? formState.modelExpectations || null : null,
        modelAdText: requestModelOption === 'ad' ? formState.modelAdText || null : null,
        models: selectedModels.map((model) => ({
          id: model.id,
          profileId: model.profileId,
          email: model.email,
          displayName: model.displayName,
          source: model.source ?? 'invite',
          status: model.status,
          splitPercent: model.splitPercent,
          guarantee: model.source === 'organizer_self' || formState.pricingType === 'free'
            ? null
            : model.guarantee !== null
              ? Math.round(model.guarantee * 100)
              : null,
        })),
        pricingType: formState.pricingType,
        currency: normalizeCurrency(formState.currency),
        ticketPrice: ticketPriceCents,
        suggestedMinimum: suggestedMinimumCents,
        modelSplitPercent: totalModelSplitPercent,
        modelGuarantee: modelGuaranteeCents,
        imageArtistCredit: formState.imageArtistCredit.trim() || null,
        imageModelCredit: formState.imageModelCredit.trim() || null,
      };

      if (options?.requireModelInviteEmails) {
        requestBody.requireModelInviteEmails = true;
      }

      if (formState.scheduledAt) {
        requestBody.scheduledAt = new Date(formState.scheduledAt).toISOString();
        requestBody.timezone = formState.timezone;

        const durationMinutes = parseDuration(formState.duration) ?? 60;
        requestBody.duration = durationMinutes;
        const startTime = new Date(formState.scheduledAt);
        const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000);
        requestBody.endsAt = endTime.toISOString();
      }

      if (formState.maxAttendees) {
        requestBody.maxAttendees = formState.maxAttendees;
      }

      let eventCode = roomCode;

      if (isEditing && roomCode) {
        const response = await fetch(`/api/rooms/${roomCode}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          throw new Error(await getErrorMessage(response, 'Failed to update event'));
        }

        const data = await response.json();
        setRoom(data.room);
        setFormState((prev) => ({
          ...prev,
          isPublic: data.room.isPublic,
        }));

        // Update guarantee hold if the amount has changed
        if (roomId) {
          const newGuaranteeCents = modelGuaranteeCents ?? 0;
          const oldGuaranteeCents = room?.modelGuarantee || 0;

          if (newGuaranteeCents !== oldGuaranteeCents) {
            try {
              await fetch('/api/stripe/update-guarantee-hold', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                  roomId,
                  roomCode,
                  newAmountCents: newGuaranteeCents,
                  oldAmountCents: oldGuaranteeCents,
                }),
              });
            } catch (error) {
              console.error('Error updating guarantee hold:', error);
            }
          }
        }
      } else {
        const response = await fetch('/api/rooms', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          throw new Error(await getErrorMessage(response, 'Failed to create event'));
        }

        const data = await response.json();
        eventCode = data.room.code;
        setRoomCode(eventCode);
        setRoomId(data.room.id);
        setRoom(data.room);
        setFormState((prev) => ({
          ...prev,
          isPublic: data.room.isPublic,
        }));

        sessionStorage.setItem(`room-token-${data.room.code}`, data.token);
        sessionStorage.setItem(`participant-id-${data.room.code}`, data.participant.id);
        sessionStorage.setItem(`participant-name-${data.room.code}`, data.participant.displayName);
        sessionStorage.setItem(`participant-role-${data.room.code}`, data.participant.role);
        sessionStorage.setItem(`participant-isHost-${data.room.code}`, 'true');
        if (data.participant.profileId) {
          sessionStorage.setItem(`participant-profileId-${data.room.code}`, data.participant.profileId);
        }
      }

      // Upload event image if provided
      if (formState.eventImage && eventCode) {
        setIsUploading(true);
        try {
          const imageFormData = new FormData();
          imageFormData.append('image', formState.eventImage);

          const imageResponse = await fetch(`/api/rooms/${eventCode}/image`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: imageFormData,
          });
          if (!imageResponse.ok) {
            throw new Error(await getErrorMessage(imageResponse, 'Failed to upload event image'));
          }
        } finally {
          setIsUploading(false);
        }
      }

      setHasUnsavedChanges(false);
      return eventCode;
    } catch (error) {
      console.error('Error saving event:', error);
      const message = error instanceof Error ? error.message : 'Failed to save event';
      alert(message);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAndContinue = async (
    nextSection: Section,
    options?: { requireModelInviteEmails?: boolean }
  ) => {
    const code = await saveRoom(undefined, options);
    if (code) {
      // Refresh room data to get updated state
      await fetchRoom(code);
      setSection(nextSection);
    }
  };

  const handleDetailsSubmit = async () => {
    if (isEditing && hasUnsavedChanges) {
      const code = await saveRoom();
      if (code) {
        await fetchRoom(code);
      }
      return;
    }

    await handleSaveAndContinue('model');
  };

  const handleSavePublishSettings = async () => {
    const isFirstPublishing = !room?.isPublished && publishDraft.isPublished;
    const code = await saveRoom(publishDraft);
    if (code) {
      await fetchRoom(code);
      if (isFirstPublishing) {
        setShowEventLiveModal(true);
      }
    }
  };

  const handleDelete = async () => {
    if (!roomCode) return;

    const confirmed = window.confirm('Are you sure you want to delete this event? This action cannot be undone.');
    if (!confirmed) return;

    setIsDeleting(true);

    try {
      const token = await getToken();
      const response = await fetch(`/api/rooms/${roomCode}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to delete event');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchDuplicateRooms = async () => {
    setDuplicateLoading(true);
    setDuplicateError(null);

    try {
      const token = await getToken();
      const response = await fetch('/api/rooms/my-events', {
        headers: authHeaders(token),
      });

      if (!response.ok) {
        throw new Error(await getErrorMessage(response, 'Failed to load past sessions'));
      }

      const data = await response.json();
      const rooms = ((data.rooms || []) as DuplicateSourceRoom[])
        .filter((candidate) => isPastDuplicateSourceRoom(candidate))
        .sort((a, b) => {
          const aTime = new Date(a.closedAt || a.endsAt || a.scheduledAt || 0).getTime();
          const bTime = new Date(b.closedAt || b.endsAt || b.scheduledAt || 0).getTime();
          return bTime - aTime;
        });

      setDuplicateRooms(rooms);
      setSelectedDuplicateCode((current) => (
        current && rooms.some((candidate) => candidate.code === current)
          ? current
          : rooms[0]?.code ?? null
      ));
    } catch (error) {
      console.error('Error loading past sessions:', error);
      setDuplicateError(error instanceof Error ? error.message : 'Failed to load past sessions');
    } finally {
      setDuplicateLoading(false);
    }
  };

  const openDuplicateModal = () => {
    setShowDuplicateModal(true);
    setDuplicateDateTime((current) => current || getDuplicateDateTimeMin());
    if (duplicateRooms.length === 0) {
      fetchDuplicateRooms();
    }
  };

  const closeDuplicateModal = () => {
    if (duplicating) return;

    setShowDuplicateModal(false);
    setDuplicateError(null);
  };

  const duplicateSelectedRoom = async () => {
    const sourceRoom = duplicateRooms.find((candidate) => candidate.code === selectedDuplicateCode);

    if (!sourceRoom) {
      setDuplicateError('Choose a past session to duplicate.');
      return;
    }

    if (!duplicateDateTime) {
      setDuplicateError('Choose a new date and time for the duplicated event.');
      return;
    }

    const scheduledAt = new Date(duplicateDateTime);
    if (!Number.isFinite(scheduledAt.getTime()) || scheduledAt.getTime() <= Date.now()) {
      setDuplicateError('Choose a future date and time for the duplicated event.');
      return;
    }

    setDuplicating(true);
    setDuplicateError(null);

    try {
      const token = await getToken();
      const response = await fetch(`/api/rooms/${sourceRoom.code}/duplicate`, {
        method: 'POST',
        headers: jsonAuthHeaders(token),
        body: JSON.stringify({
          scheduledAt: scheduledAt.toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to duplicate event');
      }

      setShowDuplicateModal(false);
      setDuplicateDateTime('');
      setSelectedDuplicateCode(null);
      setSection('details');
      setRoomCode(data.room.code);
      setRoomId(data.room.id);
      setRoom(data.room);
      setPublishDraft({
        isPublished: data.room.isPublished,
        isPublic: data.room.isPublic,
        isModelAdPublished: data.room.isModelAdPublished,
      });
      setHasUnsavedChanges(false);
      navigate('/create', {
        state: {
          roomCode: data.room.code,
          section: 'details',
        },
      });
      await fetchRoom(data.room.code);
    } catch (error) {
      console.error('Error duplicating event:', error);
      setDuplicateError(error instanceof Error ? error.message : 'Failed to duplicate event');
    } finally {
      setDuplicating(false);
    }
  };

  const navigateToSection = async (targetSection: Section) => {
    // If we have unsaved changes in details section, save first
    if (hasUnsavedChanges && section === 'details') {
      await handleSaveAndContinue(targetSection);
    } else if (!roomCode && targetSection !== 'details') {
      // Can't go to other sections without creating the event first
      alert('Please save the event details first.');
    } else if (hasUnsavedChanges && section === 'model' && targetSection === 'publish') {
      await handleSaveAndContinue(targetSection);
    } else {
      setSection(targetSection);
    }
  };

  const canAccessSection = (sectionId: Section): boolean => {
    if (sectionId === 'details') return true;
    return !!roomCode;
  };

  const renderSectionButton = (s: typeof SECTIONS[number], variant: 'mobile' | 'desktop') => {
    const isActive = section === s.id;
    const canAccess = canAccessSection(s.id as Section);
    const isComplete = s.id === 'details' ? !!roomCode :
                       s.id === 'model' ? !!roomCode :
                       s.id === 'publish' ? room?.isPublished : false;

    return (
      <button
        onClick={() => navigateToSection(s.id as Section)}
        disabled={!canAccess}
        className={[
          'flex items-center gap-2.5 rounded text-left transition-colors',
          variant === 'mobile'
            ? 'min-w-[10.75rem] flex-shrink-0 border border-line bg-panel px-3 py-2.5 shadow-sm'
            : 'w-full px-3 py-2',
          isActive
            ? 'text-text'
            : canAccess
            ? 'text-text-dim hover:text-text'
            : 'text-text-faint cursor-not-allowed',
        ].join(' ')}
      >
        <span className={`type-caption flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${
          isActive
            ? 'bg-text text-white'
            : isComplete
            ? 'bg-ok text-white'
            : 'bg-line text-text-dim'
        }`}>
          {isComplete && !isActive ? (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            s.icon
          )}
        </span>
        <span className="type-body-sm whitespace-nowrap">{s.label}</span>
      </button>
    );
  };

  const duplicateSearchQuery = duplicateSearch.trim().toLowerCase();
  const filteredDuplicateRooms = duplicateRooms.filter((candidate) => {
    if (duplicateFilter === 'tutored' && !candidate.isTutored) return false;
    if (duplicateFilter === 'untutored' && candidate.isTutored) return false;

    if (!duplicateSearchQuery) return true;

    return [
      candidate.name,
      candidate.description,
      candidate.modelName,
      candidate.scheduledAt ? formatSessionDate(candidate.scheduledAt) : null,
    ].some((value) => value?.toLowerCase().includes(duplicateSearchQuery));
  });
  const selectedDuplicateRoom = duplicateRooms.find((candidate) => candidate.code === selectedDuplicateCode) ?? null;

  const hasPublishChanges = Boolean(
    room &&
    (
      publishDraft.isPublished !== room.isPublished ||
      publishDraft.isPublic !== room.isPublic ||
      publishDraft.isModelAdPublished !== room.isModelAdPublished
    )
  );

  return (
    <div className="min-h-screen flex flex-col bg-bg">
      <NavBar variant="light" />

      {/* Main Layout - pt-[72px] to account for fixed navbar */}
      <div className="flex-1 min-h-0 pt-[72px]">
        <div className="mx-auto flex w-full max-w-[1040px] flex-col px-4 sm:px-6 lg:grid lg:grid-cols-[12rem_minmax(0,680px)] lg:gap-8 lg:px-8">
          <div className="sticky top-[72px] z-30 -mx-4 border-b border-line bg-bg/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 lg:hidden">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {SECTIONS.map((s) => (
                <div key={s.id}>
                  {renderSectionButton(s, 'mobile')}
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="hidden w-48 flex-col pt-[72px] pr-6 lg:flex">
            <nav className="flex-1">
              <ul className="space-y-0.5">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    {renderSectionButton(s, 'desktop')}
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Content */}
          <main className="w-full min-w-0 max-w-[680px] justify-self-start">
            {section === 'details' && (
              <EventDetailsStep
                formState={formState}
                onUpdate={handleUpdate}
                onSubmit={handleDetailsSubmit}
                onCancel={() => navigate('/dashboard')}
                onDelete={isEditing ? handleDelete : undefined}
                onDuplicatePastEvent={!isEditing ? openDuplicateModal : undefined}
                isSubmitting={isSubmitting}
                isUploading={isUploading}
                isEditing={isEditing}
                isDeleting={isDeleting}
                hasUnsavedChanges={hasUnsavedChanges}
              />
            )}
            {section === 'model' && roomCode && (
              <ModelPaymentStep
                formState={formState}
                onUpdate={handleUpdate}
                onNext={(options) => handleSaveAndContinue('publish', options)}
                onBack={() => setSection('details')}
                isSubmitting={isSubmitting}
                getToken={getToken}
                roomCode={roomCode}
                roomId={roomId}
                room={room}
                onRefreshRoom={fetchRoom}
              />
            )}
            {section === 'publish' && roomCode && (
              <PublishSection
                room={room}
                draft={publishDraft}
                hasChanges={hasPublishChanges}
                onUpdateDraft={(updates) => setPublishDraft((prev) => ({ ...prev, ...updates }))}
                onSave={handleSavePublishSettings}
                isSubmitting={isSubmitting}
                onBack={() => setSection('model')}
              />
            )}
            {roomCode && (room?.isPublished || room?.isModelAdPublished) && (
              <div className="mb-8 mt-6 rounded-xl border border-line bg-panel p-5 shadow-sm">
                <p className="type-body-sm text-text">
                  {room.isPublished
                    ? 'Your event is live. Prepare the room, invite guests, and see your ticket sales on the event page.'
                    : 'Your model ad is live. Review applications and manage the draft event on the event page.'}{' '}
                  <Link to={`/s/${roomCode}`} className="font-semibold underline decoration-text/30 underline-offset-4 hover:decoration-text">
                    View Event
                  </Link>
                </p>
              </div>
            )}
          </main>
        </div>
      </div>

      {showEventLiveModal && roomCode && (
        <EventLiveModal
          roomCode={roomCode}
          onClose={() => setShowEventLiveModal(false)}
        />
      )}

      {showDuplicateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4 py-6">
          <div className="flex max-h-[calc(100vh-3rem)] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-line bg-panel shadow-xl">
            <div className="border-b border-line px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-display text-[28px] leading-tight text-text">Duplicate event</h2>
                  {selectedDuplicateRoom && (
                    <p className="mt-1 text-[13px] text-text-dim">
                      Selected: <span className="text-text">{selectedDuplicateRoom.name}</span>
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={closeDuplicateModal}
                  disabled={duplicating}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-line text-text-dim transition-colors hover:border-text/30 hover:text-text disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Close duplicate event modal"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_18rem]">
              <div className="min-h-0 border-b border-line lg:border-b-0 lg:border-r">
                <div className="space-y-3 border-b border-line p-4">
                  <input
                    type="search"
                    value={duplicateSearch}
                    onChange={(event) => setDuplicateSearch(event.target.value)}
                    placeholder="Filter sessions"
                    className="input"
                  />
                  <div className="flex flex-wrap gap-2">
                    {([
                      ['all', 'All'],
                      ['tutored', 'Tutored'],
                      ['untutored', 'Untutored'],
                    ] as const).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setDuplicateFilter(value)}
                        className={`rounded-full border px-3 py-1 text-[12px] transition-colors ${
                          duplicateFilter === value
                            ? 'border-text bg-text text-white'
                            : 'border-line text-text-dim hover:border-text/30 hover:text-text'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="max-h-[42vh] overflow-y-auto p-3 lg:max-h-none">
                  {duplicateLoading ? (
                    <div className="flex min-h-44 items-center justify-center text-[13px] text-text-dim">
                      Loading past sessions...
                    </div>
                  ) : filteredDuplicateRooms.length === 0 ? (
                    <div className="flex min-h-44 flex-col items-center justify-center gap-3 text-center text-[13px] text-text-dim">
                      <div>No matching past sessions.</div>
                      {duplicateRooms.length === 0 && (
                        <button type="button" onClick={fetchDuplicateRooms} className="btn">
                          Refresh
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredDuplicateRooms.map((candidate) => {
                        const isSelected = selectedDuplicateCode === candidate.code;
                        return (
                          <button
                            key={candidate.id}
                            type="button"
                            onClick={() => setSelectedDuplicateCode(candidate.code)}
                            className={`flex w-full gap-3 rounded-lg border p-3 text-left transition-colors ${
                              isSelected
                                ? 'border-accent bg-accent-soft/70'
                                : 'border-line bg-bg hover:border-text/25'
                            }`}
                          >
                            <div className="h-16 w-20 flex-shrink-0 overflow-hidden rounded-md bg-panel2">
                              {candidate.imageUrl ? (
                                <img src={candidate.imageUrl} alt="" className="h-full w-full object-cover" />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center text-[11px] text-text-faint">
                                  No image
                                </div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start justify-between gap-2">
                                <div className="truncate text-[14px] font-medium text-text">{candidate.name}</div>
                                {candidate.isTutored && (
                                  <span className="rounded bg-accent-soft px-2 py-0.5 text-[10px] font-semibold uppercase text-accent">
                                    Tutored
                                  </span>
                                )}
                              </div>
                              <div className="mt-1 text-[12px] text-text-dim">
                                {formatSessionDate(candidate.scheduledAt)}
                                {formatSessionTime(candidate.scheduledAt) && (
                                  <span> · {formatSessionTime(candidate.scheduledAt)}</span>
                                )}
                              </div>
                              {candidate.modelName && (
                                <div className="mt-1 truncate text-[12px] text-text-faint">
                                  Model: {candidate.modelName}
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4 p-4">
                <label className="block">
                  <span className="text-[13px] font-medium text-text">New date and time</span>
                  <div className="mt-1">
                    <DateTimePicker
                      value={duplicateDateTime}
                      onChange={setDuplicateDateTime}
                      timezone={Intl.DateTimeFormat().resolvedOptions().timeZone}
                      placeholder="Start time"
                      minDateTime={getDuplicateDateTimeMin()}
                    />
                  </div>
                </label>

                {duplicateError && (
                  <div className="rounded-lg border border-warn/25 bg-warn/10 p-3 text-[13px] text-warn">
                    {duplicateError}
                  </div>
                )}

                <div className="flex flex-col gap-2 pt-2">
                  <button
                    type="button"
                    onClick={duplicateSelectedRoom}
                    disabled={duplicating || !selectedDuplicateCode || !duplicateDateTime}
                    className="btn btn-accent btn-lg justify-center disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {duplicating ? 'Duplicating...' : 'Duplicate Event'}
                  </button>
                  <button
                    type="button"
                    onClick={closeDuplicateModal}
                    disabled={duplicating}
                    className="btn justify-center disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EventLiveModal({
  roomCode,
  onClose,
}: {
  roomCode: string;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4 py-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl border border-line bg-panel p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-live-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <h2 id="event-live-modal-title" className="font-display text-[28px] leading-tight text-text">
            Your event is live.
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-line text-text-dim transition-colors hover:border-text/30 hover:text-text"
            aria-label="Close event live modal"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="mt-3 type-body-sm text-text-dim">
          Prepare the room, invite guests, and see your ticket sales on the event page.
        </p>

        <div className="mt-6 flex justify-end">
          <Link to={`/s/${roomCode}`} className="btn btn-accent">
            View Event
          </Link>
        </div>
      </div>
    </div>
  );
}
